import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Film } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const WEDDING_TYPES = ["Nikah", "Valima", "Reception", "Engagement", "Wedding", "Pre-Wedding", "Multi-Day Wedding", "Custom"];
const DURATIONS = [30, 60, 90, 120, 180, 240, 300, 360];
const STYLES = ["Cinematic", "Documentary", "Romantic Highlight", "Vintage", "Editorial", "Traditional"];
const ASPECTS = ["16:9", "9:16", "2.39:1", "4:3", "1:1"];
const RESOLUTIONS = ["720p", "1080p", "4K", "Custom"];
const PROXIES = [540, 720, 1080];

const empty = {
  name: "", bride_name: "", groom_name: "", wedding_date: "", venue: "", city: "",
  wedding_type: "Wedding", target_duration_min: 180, aspect_ratio: "16:9",
  output_resolution: "1080p", editing_style: "Cinematic", special_instructions: "", proxy_height: 540,
};

const Field = ({ label, children }) => (
  <div>
    <label className="metric-label block mb-1.5">{label}</label>
    {children}
  </div>
);

export default function NewProjectDialog({ open, onClose }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const { refreshProjects, selectProject } = useApp();
  const navigate = useNavigate();

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Project name is required"); return; }
    setSaving(true);
    try {
      const proj = await api.createProject({
        ...form,
        target_duration_min: Number(form.target_duration_min),
        proxy_height: Number(form.proxy_height),
      });
      await refreshProjects();
      selectProject(proj.id);
      toast.success(`Project "${proj.name}" created`);
      setForm(empty);
      onClose();
      navigate("/media");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString() || "Failed to create project");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        data-testid="new-project-dialog"
        className="studio-card w-full max-w-2xl max-h-[90vh] overflow-y-auto fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#252A3B] sticky top-0 bg-[#181B26] z-10">
          <div className="flex items-center gap-2.5">
            <Film size={18} className="text-[#E6B04E]" />
            <h2 className="font-head font-bold text-lg">New Wedding Project</h2>
          </div>
          <button data-testid="close-dialog" onClick={onClose} className="text-[#64748B] hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Field label="Project Name *">
              <input data-testid="input-project-name" className="studio-input" value={form.name}
                onChange={(e) => set("name", e.target.value)} placeholder="e.g. Aisha & Omar — Grand Hall" />
            </Field>
          </div>
          <Field label="Bride Name">
            <input data-testid="input-bride-name" className="studio-input" value={form.bride_name} onChange={(e) => set("bride_name", e.target.value)} />
          </Field>
          <Field label="Groom Name">
            <input data-testid="input-groom-name" className="studio-input" value={form.groom_name} onChange={(e) => set("groom_name", e.target.value)} />
          </Field>
          <Field label="Wedding Date">
            <input data-testid="input-wedding-date" type="date" className="studio-input" value={form.wedding_date} onChange={(e) => set("wedding_date", e.target.value)} />
          </Field>
          <Field label="Wedding Type">
            <select data-testid="select-wedding-type" className="studio-input" value={form.wedding_type} onChange={(e) => set("wedding_type", e.target.value)}>
              {WEDDING_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Venue">
            <input data-testid="input-venue" className="studio-input" value={form.venue} onChange={(e) => set("venue", e.target.value)} />
          </Field>
          <Field label="City">
            <input data-testid="input-city" className="studio-input" value={form.city} onChange={(e) => set("city", e.target.value)} />
          </Field>
          <Field label="Target Duration">
            <select data-testid="select-duration" className="studio-input" value={form.target_duration_min} onChange={(e) => set("target_duration_min", e.target.value)}>
              {DURATIONS.map((d) => <option key={d} value={d}>{`${d} minutes`}</option>)}
            </select>
          </Field>
          <Field label="Editing Style">
            <select data-testid="select-style" className="studio-input" value={form.editing_style} onChange={(e) => set("editing_style", e.target.value)}>
              {STYLES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Aspect Ratio">
            <select data-testid="select-aspect" className="studio-input" value={form.aspect_ratio} onChange={(e) => set("aspect_ratio", e.target.value)}>
              {ASPECTS.map((a) => <option key={a}>{a}</option>)}
            </select>
          </Field>
          <Field label="Output Resolution">
            <select data-testid="select-resolution" className="studio-input" value={form.output_resolution} onChange={(e) => set("output_resolution", e.target.value)}>
              {RESOLUTIONS.map((r) => <option key={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Proxy Quality">
            <select data-testid="select-proxy" className="studio-input" value={form.proxy_height} onChange={(e) => set("proxy_height", e.target.value)}>
              {PROXIES.map((p) => <option key={p} value={p}>{`${p}p`}</option>)}
            </select>
          </Field>
          <div className="md:col-span-2">
            <Field label="Special Editing Instructions">
              <textarea data-testid="input-instructions" rows={3} className="studio-input resize-none" value={form.special_instructions}
                onChange={(e) => set("special_instructions", e.target.value)}
                placeholder="e.g. Keep the Nikah continuous, emphasize family moments, warm color grade..." />
            </Field>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#252A3B] sticky bottom-0 bg-[#181B26]">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button data-testid="btn-submit-project" className="btn btn-gold" onClick={submit} disabled={saving}>
            {saving ? "Creating…" : "Create Project"}
          </button>
        </div>
      </div>
    </div>
  );
}
